import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Tag, Plus, Edit3, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const PALETTE = [
  "#F97316", "#10B981", "#3B82F6", "#A855F7", "#EF4444",
  "#EAB308", "#06B6D4", "#EC4899", "#6366F1", "#84CC16",
];

const EMPTY = { name: "", color: "#F97316", description: "" };

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState({ open: false, form: EMPTY, editing: null });
  const [delDlg, setDelDlg] = useState({ open: false, group: null });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/groups");
      setGroups(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => setDlg({ open: true, form: EMPTY, editing: null });
  const openEdit = (g) =>
    setDlg({
      open: true,
      form: { name: g.name, color: g.color, description: g.description || "" },
      editing: g.id,
    });

  const save = async () => {
    try {
      if (dlg.editing) {
        await api.put(`/groups/${dlg.editing}`, dlg.form);
        toast.success("Gruppe aktualisiert.");
      } else {
        await api.post("/groups", dlg.form);
        toast.success("Gruppe angelegt.");
      }
      setDlg({ open: false, form: EMPTY, editing: null });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/groups/${delDlg.group.id}`);
      toast.success("Gruppe gelöscht.");
      setDelDlg({ open: false, group: null });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-[900px] mx-auto space-y-6" data-testid="groups-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="mono text-[11px] tracking-[0.25em] text-zinc-500 mb-2">
            ARTIKEL-KATEGORIEN
          </div>
          <h1 className="font-display text-4xl uppercase flex items-center gap-3">
            <Tag className="w-8 h-8" /> Gruppen
          </h1>
          <p className="text-sm text-zinc-600 mt-2">
            Strukturiere Artikel in Gruppen (z. B. „Federn", „Schrauben", „Motoren") für Filter und
            Auswertungen.
          </p>
        </div>
        <Button
          onClick={openNew}
          data-testid="new-group-button"
          className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm h-11 px-6 font-semibold tracking-wide"
        >
          <Plus className="w-4 h-4 mr-2" /> NEUE GRUPPE
        </Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center mono text-xs text-zinc-500">LADEN…</div>
        ) : groups.length === 0 ? (
          <div className="p-12 text-center">
            <Tag className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm text-zinc-600">
              Noch keine Gruppen angelegt. Klicke auf „Neue Gruppe", um zu starten.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-zinc-100">
              <tr>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">FARBE</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">NAME</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">BESCHREIBUNG</th>
                <th className="text-right px-4 py-3 mono text-[10px] tracking-[0.2em]">ARTIKEL</th>
                <th className="text-right px-4 py-3 mono text-[10px] tracking-[0.2em]">AKTIONEN</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`group-row-${g.id}`}>
                  <td className="px-4 py-3">
                    <div
                      className="w-6 h-6 rounded-sm border border-zinc-300"
                      style={{ backgroundColor: g.color }}
                    />
                  </td>
                  <td className="px-4 py-3 font-medium">{g.name}</td>
                  <td className="px-4 py-3 text-zinc-600">{g.description || "—"}</td>
                  <td className="px-4 py-3 text-right mono">{g.article_count}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(g)}
                        data-testid={`edit-group-${g.id}`}
                        className="p-2 hover:bg-zinc-100 rounded-sm"
                        title="Bearbeiten"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDelDlg({ open: true, group: g })}
                        data-testid={`del-group-${g.id}`}
                        className="p-2 hover:bg-red-100 hover:text-red-700 rounded-sm"
                        title="Löschen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <AlertDialog open={dlg.open} onOpenChange={(o) => !o && setDlg({ open: false, form: EMPTY, editing: null })}>
        <AlertDialogContent className="rounded-sm" data-testid="group-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{dlg.editing ? "Gruppe bearbeiten" : "Neue Gruppe"}</AlertDialogTitle>
            <AlertDialogDescription>
              Eine Gruppe bündelt Artikel mit ähnlicher Funktion (z. B. „Federn", „Schrauben").
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Name *</Label>
              <Input
                value={dlg.form.name}
                onChange={(e) => setDlg((s) => ({ ...s, form: { ...s.form, name: e.target.value } }))}
                placeholder="z. B. Federn, Motoren, Schrauben"
                data-testid="group-name-input"
                className="mt-1.5 rounded-sm border-zinc-400"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Farbe</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setDlg((s) => ({ ...s, form: { ...s.form, color: c } }))}
                    data-testid={`group-color-${c}`}
                    className={`w-8 h-8 rounded-sm border-2 transition-all ${
                      dlg.form.color === c ? "border-black scale-110" : "border-transparent"
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Beschreibung (optional)</Label>
              <Textarea
                value={dlg.form.description}
                onChange={(e) =>
                  setDlg((s) => ({ ...s, form: { ...s.form, description: e.target.value } }))
                }
                rows={2}
                data-testid="group-desc-input"
                className="mt-1.5 rounded-sm border-zinc-400"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={save}
              data-testid="group-save-button"
              className="bg-[#F97316] hover:bg-[#EA580C] rounded-sm"
            >
              <Save className="w-4 h-4 mr-2" /> Speichern
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirm */}
      <AlertDialog open={delDlg.open} onOpenChange={(o) => !o && setDelDlg({ open: false, group: null })}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Gruppe löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Gruppe „<strong>{delDlg.group?.name}</strong>" wird gelöscht.
              {delDlg.group?.article_count > 0 && (
                <>
                  {" "}
                  <strong>{delDlg.group.article_count} Artikel</strong> haben aktuell diese Gruppe — die werden danach „ohne Gruppe" angezeigt.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="group-del-confirm"
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
